<script setup lang="ts">
/**
 * Reset your password (Figma `66:46908` … `66:46966`).
 *
 * Submit is gated on a validly formatted address, same client-side rule as sign
 * up (`66:47506`). Once submitted the flow always lands on "Check your email" —
 * including when the address belongs to nobody. Branching on the answer would
 * tell an attacker which addresses have accounts (`66:47515`).
 */
import { FairButton } from '@/ui/primitives/FairButton'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthModal, useUserStore } from '@/features/auth/store'
import { requestPasswordReset } from '@/features/auth/api/password'
import { isEmail } from '@/features/auth/model/passwordRules'
import { mapAuthError } from '@/features/auth/model/mapAuthError'
import { useAuthFormValidation } from '@/features/auth/model/useAuthFormValidation'
import AuthField from '@/features/auth/ui/parts/AuthField.vue'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import { useFairToast } from '@/ui/patterns/FairToast'

const { t } = useI18n()
const toast = useFairToast()
const authModal = useAuthModal()
const userStore = useUserStore()

const email = ref('')
const touched = ref(false)

const formatInvalid = computed(() =>
  touched.value && email.value.trim() !== '' && !isEmail(email.value),
)

const clientValid = computed(() => isEmail(email.value))
const form = useAuthFormValidation({ clientValid })

async function handleSubmit() {
  await form.submit(async () => {
    const address = email.value.trim()
    try {
      await requestPasswordReset(address)
    } catch (error) {
      const mapped = mapAuthError(error, 'forgotPassword')
      // A real transport failure is worth reporting; a 4xx is not, because the
      // answer itself must not reveal whether the account exists.
      if (mapped.status === undefined || mapped.status >= 500 || mapped.status === 429) {
        toast.show({ message: t(mapped.messageKey), tone: 'warning' })
        return
      }
    }

    userStore.setTempEmailForPasswordReset(address)
    authModal.showAuthModal('checkEmail')
  })
}
</script>

<template>
  <AuthScreenShell
    :title="t('auth.forgot.title')"
    :description="t('auth.forgot.description')"
  >
    <form
      class="fair-auth-form"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <AuthField
        v-model="email"
        input-id="auth-forgot-email"
        type="email"
        autocomplete="email"
        :label="t('auth.forgot.email_label')"
        :invalid="formatInvalid"
        :error-text="formatInvalid ? t('auth.errors.invalid_email') : undefined"
        @blur="touched = true"
      />

      <FairButton
      class="fair-auth-cta"
      size="auto"
        type="submit"
        :label="t('auth.forgot.submit')"
        :disabled="form.submitDisabled.value"
      />
    </form>

    <p class="fair-auth-switch">
      <button
        type="button"
        class="fair-auth-form__link"
        @click="authModal.showAuthModal('login')"
      >
        {{ t('auth.forgot.back_to_login') }}
      </button>
    </p>
  </AuthScreenShell>
</template>
